/**
 * StreakCalendarContext — shares the "open the month-streak overlay" action
 * across the home shell.
 *
 * The streak entry points live in two separate React subtrees: the streak pill
 * is in the shell Header (HomeHeader/Header.tsx, rendered by HomeLayout), and
 * the week strip is inside the LearnPage content. iOS keeps both in one
 * component (WeeklyCalendar.tsx) so a single onStreakPress toggles the overlay
 * (:218-221, :228). On web we bridge the two subtrees with this context: the
 * provider wraps HomeLayout, fetches the streak/join data once, and renders the
 * <MonthStreakCalendar/> overlay; both entry points call `open()`.
 */
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { callWithAuth, getUserInfo, getUserStats } from "@/api/Api";
import MonthStreakCalendar from "./MonthStreakCalendar";

interface StreakCalendarContextValue {
  open: () => void;
  close: () => void;
}

const StreakCalendarContext = createContext<StreakCalendarContextValue | null>(
  null,
);

/** Opens the month-streak overlay. Safe no-op outside the provider so callers
 *  (Header / strip) never crash if rendered standalone. */
// eslint-disable-next-line react-refresh/only-export-components
export function useStreakCalendar(): StreakCalendarContextValue {
  return useContext(StreakCalendarContext) ?? { open: () => {}, close: () => {} };
}

export const StreakCalendarProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);

  // Shared cache keys with HomeLayout/Header/LearnPage — no extra fetch.
  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });
  // Prefetch (not gated on `open`): iOS passes the already-hydrated store value
  // into the calendar, so the "YOUR STREAK 🔥 N days" header is correct on the
  // very first open instead of briefly flashing the floor value 1 while the
  // stats request resolves. Shares the cache key with Header/LearnPage.
  const userStatsQuery = useQuery({
    queryKey: ["userStats"],
    queryFn: () => callWithAuth(getUserStats),
  });

  // Floor to 1 (iOS AppTopBar.tsx:43 / Header.tsx:50): today is the first day.
  const dayStreak = userStatsQuery.data?.dayStreak || 1;

  const openCalendar = useCallback(() => setOpen(true), []);
  // Closing is event-driven: the nav tabs call this before navigating (see
  // NavBar) so the overlay never lingers over the next tab — without a
  // "reset state in useEffect" anti-pattern on route change.
  const closeCalendar = useCallback(() => setOpen(false), []);

  return (
    <StreakCalendarContext.Provider
      value={{ open: openCalendar, close: closeCalendar }}
    >
      {children}
      {open && (
        <MonthStreakCalendar
          dayStreak={dayStreak}
          joinDate={userInfoQuery.data?.joinDate}
          userId={userInfoQuery.data?.id}
          onClose={() => setOpen(false)}
        />
      )}
    </StreakCalendarContext.Provider>
  );
};

export default StreakCalendarProvider;
